import React, { useMemo } from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const urgencyColors = {
  urgent: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  important: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30' },
  normal: { bg: 'bg-primary/20', text: 'text-primary', border: 'border-primary/30' },
};

const WhosNextWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  const pending = useMemo(() => {
    const pendingContributions = state?.pendingContributions ?? [];
    return pendingContributions
      .filter(c => c.state !== 'accepted' && c.state !== 'ignored')
      .sort((a, b) => {
        const urgencyOrder: Record<string, number> = { urgent: 0, important: 1, normal: 2 };
        return (urgencyOrder[a.urgency] || 2) - (urgencyOrder[b.urgency] || 2);
      });
  }, [state?.pendingContributions]);

  if (!state) return null;

  const sessionAvatars = state.sessionAvatars ?? [];
  const getAvatar = (role: string) => sessionAvatars.find(a => a.role === role);

  if (pending.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${
        isDark ? 'text-white/30' : 'text-slate-400'
      }`}>
        No pending contributions
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full px-3 py-2 gap-2 overflow-y-auto">
      {pending.map((contribution) => {
        const avatar = getAvatar(contribution.role);
        const colors = urgencyColors[contribution.urgency] || urgencyColors.normal;

        return (
          <div
            key={contribution.contributionId ?? `${contribution.role}-${contribution.createdAt ?? contribution.urgency}`}
            className={`flex items-start gap-2.5 p-2 rounded-lg border ${
              isDark
                ? 'bg-white/[0.03] border-white/5'
                : 'bg-slate-50 border-slate-100'
            }`}
          >
            {/* Avatar */}
            <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 border border-white/10">
              {avatar?.imageUrl ? (
                <img src={avatar.imageUrl} alt={avatar.name} className="w-full h-full object-cover" />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-xs font-bold"
                  style={{ backgroundColor: avatar?.primaryColor || '#475569', color: '#fff' }}
                >
                  {avatar?.emoji || avatar?.name?.charAt(0) || '?'}
                </div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              {/* Name + urgency badge */}
              <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                  {avatar?.name || contribution.role}
                </span>
                <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded border ${colors.bg} ${colors.text} ${colors.border}`}>
                  {contribution.urgency}
                </span>
              </div>

              {/* Preview */}
              <p className={`text-xs mt-0.5 line-clamp-2 ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
                {contribution.preview || contribution.response}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WhosNextWidget;
